import React, { useEffect, useMemo, useState } from "react";
import { listGuides, ListResult } from "../../data/guidesService";
import { GuideStatus } from "../../types/guide";
import GuideStatusBadge from "../../components/GuideStatusBadge";
import GuideDetailModal from "./GuideDetailModal";
import GuideStats from "../../components/GuideStats";
import GuideProvisionModal from "../../components/GuideProvisionModal";
import DeleteUserModal from "../../components/DeleteUserModal";
import { getSupabaseClient } from "../../lib/supabase";
import type { Guide } from "../../types/guide";

const CONNECTED_ORDER_STATUSES = [
  "paid_pending_delivery",
  "delivered",
];

const normalizeInstallmentStatus = (status?: string | null) => {
  if (!status) return "scheduled";
  const normalized = status.toLowerCase();
  if (["paid", "succeeded", "completed"].includes(normalized)) return "paid";
  if (["scheduled", "pending", "pending_payment", "requires_payment_method", "active"].includes(normalized)) {
    return "scheduled";
  }
  if (["processing", "in_progress"].includes(normalized)) return "processing";
  if (["failed", "canceled", "cancelled", "overdue"].includes(normalized)) return "failed";
  return normalized;
};

async function hydrateGuideStats(items: Guide[]): Promise<Guide[]> {
  const guideIds = items.map((g) => g.id).filter((id): id is string => !!id);
  if (guideIds.length === 0) return items;

  const supabase = getSupabaseClient();

  // Fetch all active matches for connected seekers count
  const { data: matchesData, error: matchesError } = await supabase
    .from("matches")
    .select("guide_id, seeker_id")
    .eq("status", "active");

  if (matchesError) {
    console.warn("Failed to load matches:", matchesError);
  }

  // Build seeker map from active matches
  const seekerMap = new Map<string, Set<string>>();
  (matchesData ?? []).forEach((match) => {
    if (!match.guide_id || !match.seeker_id) return;
    if (!seekerMap.has(match.guide_id)) {
      seekerMap.set(match.guide_id, new Set());
    }
    seekerMap.get(match.guide_id)!.add(match.seeker_id);
  });

  // Fetch orders for income calculation
  const { data: ordersData, error: ordersError } = await supabase
    .from("orders")
    .select("id, guide_id, seeker_id, status, total_cents")
    .in("guide_id", guideIds)
    .in("status", CONNECTED_ORDER_STATUSES);

  if (ordersError) {
    console.warn("Failed to load order stats:", ordersError);
  }

  const orderIds = Array.from(new Set((ordersData ?? []).map((order) => order.id).filter((id): id is string => !!id)));

  const { data: installmentsData, error: installmentsError } = orderIds.length
    ? await supabase
        .from("order_installments")
        .select("order_id, amount_cents, status")
        .in("order_id", orderIds)
    : { data: [], error: null };

  if (installmentsError) {
    console.warn("Failed to load order installments:", installmentsError);
  }

  const installmentsTotalMap = new Map<string, number>();
  const installmentsPaidMap = new Map<string, number>();
  (installmentsData ?? []).forEach((inst) => {
    if (!inst.order_id) return;
    const totalPrev = installmentsTotalMap.get(inst.order_id) ?? 0;
    installmentsTotalMap.set(inst.order_id, totalPrev + (inst.amount_cents ?? 0));

    if (normalizeInstallmentStatus(inst.status) === "paid") {
      const paidPrev = installmentsPaidMap.get(inst.order_id) ?? 0;
      installmentsPaidMap.set(inst.order_id, paidPrev + (inst.amount_cents ?? 0));
    }
  });

  const incomeMap = new Map<string, number>();
  (ordersData ?? []).forEach((order) => {
    if (!order.guide_id) return;
    const totalCents = Number(order.total_cents ?? 0);
    const installmentsTotal = installmentsTotalMap.get(order.id) ?? 0;
    const depositCents = Math.max(totalCents - installmentsTotal, 0);
    const paidInstallments = installmentsPaidMap.get(order.id) ?? 0;
    const depositPaid = CONNECTED_ORDER_STATUSES.includes(order.status);
    const earnedCents = (depositPaid ? depositCents : 0) + paidInstallments;
    const prev = incomeMap.get(order.guide_id) ?? 0;
    incomeMap.set(order.guide_id, prev + earnedCents);
  });

  return items.map((guide) => {
    const seekers = seekerMap.get(guide.id) ?? new Set<string>();
    const incomeCents = incomeMap.get(guide.id) ?? 0;
    return {
      ...guide,
      connectedSeekers: seekers.size,
      income: Number((incomeCents / 100).toFixed(2)),
    };
  });
}

const STATUS_OPTIONS: Array<GuideStatus | "all"> = [
  "all",
  "pending",
  "pending_activation",
  "consult_required",
  "approved",
  "active",
  "not_fit",
];

type SortField = "name" | "status" | "connectedSeekers" | "income" | "joinedAt";
type SortDirection = "asc" | "desc";

export default function GuideListPage() {
  // UI state
  const [q, setQ] = useState("");
  const [status, setStatus] = useState<GuideStatus | "all">("all");
  const [incomeMin, setIncomeMin] = useState("");
  const [incomeMax, setIncomeMax] = useState("");

  // Filter modal state
  const [showFilters, setShowFilters] = useState(false);
  const [draftStatus, setDraftStatus] = useState<GuideStatus | "all">("all");
  const [draftIncomeMin, setDraftIncomeMin] = useState("");
  const [draftIncomeMax, setDraftIncomeMax] = useState("");

  // Pagination state
  const [page, setPage] = useState(1);
  const [pageSize] = useState(15);

  // Data state
  const [res, setRes] = useState<ListResult | null>(null);
  const [loading, setLoading] = useState(false);
  const [showCreateGuide, setShowCreateGuide] = useState(false);
  const [provisionAlert, setProvisionAlert] = useState<{ text: string; type: "success" | "error" } | null>(null);

  // Modal state
  const [openId, setOpenId] = useState<string | null>(null);
  const [deleteTarget, setDeleteTarget] = useState<Guide | null>(null);

  // Sorting and selection state
  const [sortField, setSortField] = useState<SortField>("joinedAt");
  const [sortDirection, setSortDirection] = useState<SortDirection>("desc");
  const [selectedIds, setSelectedIds] = useState<Set<string>>(new Set());
  const [showBulkActions, setShowBulkActions] = useState(false);

  const totalPages = useMemo(() => {
    if (!res) return 1;
    return Math.max(1, Math.ceil(res.total / pageSize));
  }, [res, pageSize]);

  // Items are already sorted (either by backend or client-side in useEffect)
  const sortedItems = res?.items ?? [];

  const allSelected = sortedItems.length > 0 && sortedItems.every(g => selectedIds.has(g.id));
  const someSelected = selectedIds.size > 0 && !allSelected;

  const formatIncome = (value: number) =>
    Number.isFinite(value)
      ? value.toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })
      : "0.00";

  useEffect(() => {
    let isMounted = true;

    async function load() {
      try {
        setLoading(true);

        // For connectedSeekers sorting, we need to fetch all, hydrate, sort, then paginate client-side
        if (sortField === "connectedSeekers") {
          // Fetch all guides (use large pageSize)
          const result = await listGuides({
            q,
            status,
            incomeMin: incomeMin ? Number(incomeMin) : undefined,
            incomeMax: incomeMax ? Number(incomeMax) : undefined,
            page: 1,
            pageSize: 10000, // Fetch all
            sortBy: "joinedAt", // Default sort, we'll sort client-side
            sortDirection: "desc",
          });

          const hydratedItems = await hydrateGuideStats(result.items);
          if (!isMounted) return;

          // Sort by connectedSeekers client-side
          const sorted = [...hydratedItems].sort((a, b) => {
            const aVal = a.connectedSeekers ?? 0;
            const bVal = b.connectedSeekers ?? 0;
            return sortDirection === "asc" ? aVal - bVal : bVal - aVal;
          });

          // Paginate client-side
          const start = (page - 1) * pageSize;
          const paginatedItems = sorted.slice(start, start + pageSize);

          setRes({ items: paginatedItems, total: result.total });
        } else {
          // For other sort fields, use backend pagination
          const result = await listGuides({
            q,
            status,
            incomeMin: incomeMin ? Number(incomeMin) : undefined,
            incomeMax: incomeMax ? Number(incomeMax) : undefined,
            page,
            pageSize,
            sortBy: sortField,
            sortDirection,
          });

          const hydratedItems = await hydrateGuideStats(result.items);
          if (!isMounted) return;
          setRes({ items: hydratedItems, total: result.total });
        }
      } catch (err) {
        console.error("Failed to load guides:", err);
      } finally {
        if (isMounted) setLoading(false);
      }
    }

    load();
    return () => {
      isMounted = false;
    };
  }, [q, status, incomeMin, incomeMax, page, pageSize, sortField, sortDirection]);

  async function refreshSamePage() {
    setLoading(true);
    try {
      if (sortField === "connectedSeekers") {
        // Fetch all, hydrate, sort, paginate client-side
        const result = await listGuides({
          q,
          status,
          incomeMin: incomeMin ? Number(incomeMin) : undefined,
          incomeMax: incomeMax ? Number(incomeMax) : undefined,
          page: 1,
          pageSize: 10000,
          sortBy: "joinedAt",
          sortDirection: "desc",
        });

        const hydratedItems = await hydrateGuideStats(result.items);
        const sorted = [...hydratedItems].sort((a, b) => {
          const aVal = a.connectedSeekers ?? 0;
          const bVal = b.connectedSeekers ?? 0;
          return sortDirection === "asc" ? aVal - bVal : bVal - aVal;
        });

        const start = (page - 1) * pageSize;
        const paginatedItems = sorted.slice(start, start + pageSize);
        setRes({ items: paginatedItems, total: result.total });
      } else {
        const result = await listGuides({
          q,
          status,
          incomeMin: incomeMin ? Number(incomeMin) : undefined,
          incomeMax: incomeMax ? Number(incomeMax) : undefined,
          page,
          pageSize,
          sortBy: sortField,
          sortDirection,
        });

        const hydratedItems = await hydrateGuideStats(result.items);
        setRes({ items: hydratedItems, total: result.total });
      }
    } finally {
      setLoading(false);
    }
  }

  function handleSort(field: SortField) {
    if (sortField === field) {
      setSortDirection(sortDirection === "asc" ? "desc" : "asc");
    } else {
      setSortField(field);
      setSortDirection("asc");
    }
  }

  function toggleSelectAll() {
    if (allSelected) {
      setSelectedIds(new Set());
    } else {
      setSelectedIds(new Set(sortedItems.map(g => g.id)));
    }
  }

  function toggleSelect(id: string) {
    const newSelected = new Set(selectedIds);
    if (newSelected.has(id)) {
      newSelected.delete(id);
    } else {
      newSelected.add(id);
    }
    setSelectedIds(newSelected);
  }

  function handleExportTable() {
    const selectedGuides = sortedItems.filter(g => selectedIds.has(g.id));
    const csv = [
      ["Name", "Email", "Status", "Connected Seekers", "Income", "Joined"].join(","),
      ...selectedGuides.map(g => [
        g.name,
        g.email,
        g.status,
        g.connectedSeekers,
        g.income,
        new Date(g.joinedAt).toLocaleDateString(),
      ].join(","))
    ].join("\n");

    const blob = new Blob([csv], { type: "text/csv" });
    const url = URL.createObjectURL(blob);
    const a = document.createElement("a");
    a.href = url;
    a.download = `guides-export-${new Date().toISOString().split('T')[0]}.csv`;
    a.click();
    URL.revokeObjectURL(url);
    setShowBulkActions(false);
  }

  async function handleExportAllData() {
    const selectedGuides = sortedItems.filter(g => selectedIds.has(g.id));

    if (selectedGuides.length === 0) {
      alert('No guides selected');
      return;
    }

    setShowBulkActions(false);

    // Show loading indicator
    const loadingMsg = `Exporting data for ${selectedGuides.length} guide${selectedGuides.length > 1 ? 's' : ''}...`;
    console.log(loadingMsg);

    const supabase = getSupabaseClient();

    for (const guide of selectedGuides) {
      try {
        // Fetch comprehensive data for this guide
        const guideUserId = guide.id; // This is the auth user ID

        // Fetch guide onboarding data
        const { data: onboardingData } = await supabase
          .from('guide_onboarding')
          .select('*')
          .eq('user_id', guideUserId)
          .maybeSingle();

        // Fetch active matches (seekers they're matched with)
        const { data: activeMatches } = await supabase
          .from('matches')
          .select(`
            *,
            seeker_details:seeker_id(
              id,
              email
            ),
            change_request:match_change_requests(
              id,
              initiator,
              reason_id,
              reason_label,
              details,
              status,
              created_at,
              decided_at
            )
          `)
          .eq('guide_id', guideUserId)
          .or('status.eq.active,status.is.null');

        // Fetch match history
        const { data: matchHistory } = await supabase
          .from('matches')
          .select(`
            *,
            change_request:match_change_requests(
              id,
              initiator,
              reason_id,
              reason_label,
              details,
              status,
              created_at,
              decided_at
            )
          `)
          .eq('guide_id', guideUserId)
          .order('created_at', { ascending: false });

        // Fetch sessions
        const { data: sessions } = await supabase
          .from('sessions')
          .select('*')
          .eq('guide_id', guideUserId)
          .order('session_date', { ascending: false });

        // Fetch packages created by this guide
        const { data: packages } = await supabase
          .from('packages')
          .select('*')
          .eq('guide_id', guideUserId)
          .order('created_at', { ascending: false });

        // Fetch package offers
        const { data: packageOffers } = await supabase
          .from('package_offers')
          .select('*')
          .eq('guide_id', guideUserId)
          .order('created_at', { ascending: false });

        // Fetch notes written by this guide
        const { data: notes } = await supabase
          .from('notes')
          .select('*')
          .eq('guide_id', guideUserId)
          .order('created_at', { ascending: false });

        // Fetch profile data for additional info
        const { data: profileData } = await supabase
          .from('profiles')
          .select('full_name, phone, email, seeker_initiated_changes, guide_initiated_changes')
          .eq('id', guideUserId)
          .maybeSingle();

        // Build export payload
        const basicGuideInfo = {
          id: guide.id,
          name: profileData?.full_name ?? guide.name,
          email: profileData?.email ?? guide.email,
          phone: profileData?.phone ?? null,
          status: guide.status,
          connectedSeekers: guide.connectedSeekers,
          income: guide.income,
          joinedAt: guide.joinedAt,
          seekerInitiatedChanges: profileData?.seeker_initiated_changes ?? null,
          guideInitiatedChanges: profileData?.guide_initiated_changes ?? null,
        };

        const onboardingInfo = onboardingData ? {
          sessionId: onboardingData.session_id ?? null,
          professionalTitle: onboardingData.professional_title ?? null,
          yearsExperience: onboardingData.years_experience ?? null,
          specializations: onboardingData.specializations ?? null,
          experienceTypes: onboardingData.experience_types ?? null,
          supportTypes: onboardingData.support_types ?? null,
          focusAreas: onboardingData.focus_areas ?? null,
          specialGroups: onboardingData.special_groups ?? null,
          religiousBeliefs: onboardingData.religious_beliefs ?? null,
          locationPostcode: onboardingData.location_postcode ?? null,
          workingRadius: onboardingData.working_radius ?? null,
          profileBio: onboardingData.profile_bio ?? null,
          backgroundCheck: onboardingData.background_check ?? null,
          backgroundCheckVerified: onboardingData.background_check_verified ?? null,
          licenseNumber: onboardingData.license_number ?? null,
          licensingBody: onboardingData.licensing_body ?? null,
          insuranceProvider: onboardingData.insurance_provider ?? null,
          insurancePolicyNumber: onboardingData.insurance_policy_number ?? null,
          onboardingStartedAt: onboardingData.created_at ?? null,
          onboardingCompletedAt: onboardingData.updated_at ?? null,
          onboardingStatus: onboardingData.status ?? null,
        } : null;

        const activeMatchesExport = (activeMatches ?? []).map((match) => ({
          id: match.id,
          seekerId: match.seeker_id,
          seekerDetails: match.seeker_details ?? null,
          status: match.status ?? null,
          createdAt: match.created_at,
          updatedAt: match.updated_at ?? null,
          endedAt: match.ended_at ?? null,
          matchScore: match.match_score,
          scoreBreakdown: match.score_breakdown,
          reasoning: match.reasoning,
          changeRequest: match.change_request ? {
            id: match.change_request.id,
            initiator: match.change_request.initiator,
            reasonId: match.change_request.reason_id ?? null,
            reasonLabel: match.change_request.reason_label ?? null,
            details: match.change_request.details ?? null,
            status: match.change_request.status ?? null,
            createdAt: match.change_request.created_at ?? null,
            decidedAt: match.change_request.decided_at ?? null,
          } : null,
        }));

        const matchHistoryExport = (matchHistory ?? []).map((historyMatch) => ({
          id: historyMatch.id,
          seekerId: historyMatch.seeker_id,
          status: historyMatch.status ?? null,
          createdAt: historyMatch.created_at,
          updatedAt: historyMatch.updated_at ?? null,
          endedAt: historyMatch.ended_at ?? null,
          matchScore: historyMatch.match_score,
          scoreBreakdown: historyMatch.score_breakdown,
          reasoning: historyMatch.reasoning,
          changeRequest: historyMatch.change_request ? {
            id: historyMatch.change_request.id,
            initiator: historyMatch.change_request.initiator,
            reasonId: historyMatch.change_request.reason_id ?? null,
            reasonLabel: historyMatch.change_request.reason_label ?? null,
            details: historyMatch.change_request.details ?? null,
            status: historyMatch.change_request.status ?? null,
            createdAt: historyMatch.change_request.created_at ?? null,
            decidedAt: historyMatch.change_request.decided_at ?? null,
          } : null,
        }));

        const sessionData = (sessions ?? []).map((session) => ({
          id: session.id,
          guideId: session.guide_id,
          seekerId: session.seeker_id,
          sessionType: session.session_type,
          sessionDate: session.session_date,
          sessionTime: session.session_time,
          durationMinutes: session.duration_minutes,
          sessionStatus: session.session_status,
          notes: session.notes,
          createdAt: session.created_at,
          updatedAt: session.updated_at,
        }));

        const packagesData = (packages ?? []).map((pkg) => ({
          id: pkg.id,
          guideId: pkg.guide_id,
          name: pkg.name,
          description: pkg.description,
          totalSessions: pkg.total_sessions,
          pricePerSession: pkg.price_per_session,
          totalPrice: pkg.total_price,
          isActive: pkg.is_active,
          createdAt: pkg.created_at,
          updatedAt: pkg.updated_at,
        }));

        const packageOffersData = (packageOffers ?? []).map((offer) => ({
          id: offer.id,
          guideId: offer.guide_id,
          seekerId: offer.seeker_id,
          packageId: offer.package_id,
          status: offer.status,
          offerPrice: offer.offer_price,
          notes: offer.notes,
          createdAt: offer.created_at,
          updatedAt: offer.updated_at,
          acceptedAt: offer.accepted_at,
          rejectedAt: offer.rejected_at,
        }));

        const notesData = (notes ?? []).map((note) => ({
          id: note.id,
          guideId: note.guide_id,
          seekerId: note.seeker_id,
          content: note.content,
          isPrivate: note.is_private,
          createdAt: note.created_at,
          updatedAt: note.updated_at,
        }));

        const exportPayload = {
          generatedAt: new Date().toISOString(),
          guide: basicGuideInfo,
          onboarding: onboardingInfo,
          activeMatches: activeMatchesExport,
          matchHistory: matchHistoryExport,
          sessions: sessionData,
          packages: packagesData,
          packageOffers: packageOffersData,
          notes: notesData,
        };

        // Download JSON file for this guide
        const blob = new Blob([JSON.stringify(exportPayload, null, 2)], { type: 'application/json' });
        const url = URL.createObjectURL(blob);
        const anchor = document.createElement('a');
        anchor.href = url;
        const safeName = guide.name
          ? guide.name.toLowerCase().replace(/[^a-z0-9]+/g, '-').replace(/^-|-$/g, '')
          : 'guide';
        anchor.download = `guide-${safeName || guide.id}-export.json`;
        document.body.appendChild(anchor);
        anchor.click();
        document.body.removeChild(anchor);
        setTimeout(() => URL.revokeObjectURL(url), 100);

        // Add small delay between downloads to prevent browser blocking
        await new Promise(resolve => setTimeout(resolve, 300));
      } catch (error) {
        console.error(`Failed to export data for guide ${guide.name || guide.id}:`, error);
        alert(`Failed to export data for guide ${guide.name || guide.id}`);
      }
    }

    alert(`Export complete for ${selectedGuides.length} guide${selectedGuides.length > 1 ? 's' : ''}`);
  }

  function SortIcon({ field }: { field: SortField }) {
    if (sortField !== field) {
      return (
        <svg className="w-4 h-4 opacity-30" fill="none" viewBox="0 0 24 24" stroke="currentColor">
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M7 16V4m0 0L3 8m4-4l4 4m6 0v12m0 0l4-4m-4 4l-4-4" />
        </svg>
      );
    }
    if (sortDirection === "asc") {
      return (
        <svg className="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor">
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 15l7-7 7 7" />
        </svg>
      );
    }
    return (
      <svg className="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor">
        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
      </svg>
    );
  }

  useEffect(() => {
    if (!provisionAlert) return;
    const timer = setTimeout(() => setProvisionAlert(null), 6000);
    return () => clearTimeout(timer);
  }, [provisionAlert]);

  return (
    <div className="min-h-full bg-[#FFFCEF] text-[#2C2731] p-6 space-y-6">
      {/* Header */}
      <header className="flex flex-col gap-3 md:flex-row md:items-center md:justify-between">
        <div>
          <h1 className="text-3xl font-medium text-[#2C2731] font-recoleta">
            Guides Management
          </h1>
          <p className="text-sm text-[#2C2731]/80 md:text-base">
            Manage and monitor all registered guides
          </p>
        </div>

        <button
          type="button"
          onClick={() => setShowCreateGuide(true)}
          className="inline-flex items-center gap-3 rounded-lg border border-[#2C2731] bg-[#2C2731] px-4 py-2.5 text-sm font-medium text-[#FEF6D6] shadow-md transition-colors hover:bg-[#3C3450]"
        >
          <span className="flex h-6 w-6 items-center justify-center rounded-full bg-[#FFFCEF]/20 text-lg font-semibold">
            +
          </span>
          Add Guide
        </button>
      </header>

      {provisionAlert && (
        <div
          className={`rounded-2xl border px-4 py-3 text-sm ${
            provisionAlert.type === "success"
              ? "border-[#214D3C]/40 bg-[#E4F1E8] text-[#214D3C]"
              : "border-[#B24E4E]/40 bg-[#FCEDEA] text-[#8C2F2F]"
          } flex items-start justify-between gap-4`}
        >
          <p>{provisionAlert.text}</p>
          <button
            type="button"
            onClick={() => setProvisionAlert(null)}
            className="text-xs font-semibold uppercase tracking-wide text-[#2C2731]"
          >
            Dismiss
          </button>
        </div>
      )}

      <GuideStats />

      {/* Toolbar */}
      <div className="flex flex-col gap-4 rounded-xl border border-[#2C2731] bg-[#2C2731] p-4 text-[#FEF6D6] shadow-sm lg:flex-row lg:items-center lg:justify-between">
        <div className="flex items-center gap-3">
          <div className="text-lg font-medium font-recoleta">All Guides</div>
          {selectedIds.size > 0 && (
            <span className="text-sm text-[#FEF6D6]/70">
              ({selectedIds.size} selected)
            </span>
          )}
        </div>
        <div className="flex w-full flex-col gap-3 sm:flex-row sm:items-center sm:justify-end">
          {selectedIds.size > 0 && (
            <div className="relative">
              <button
                onClick={() => setShowBulkActions(!showBulkActions)}
                className="inline-flex items-center justify-center gap-2 rounded-lg border border-[#FEF6D6] bg-[#2C2731] px-3 py-2 text-sm font-medium text-[#FEF6D6] transition-colors hover:bg-[#3C3450]"
              >
                Bulk Actions
                <svg className="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
                </svg>
              </button>
              {showBulkActions && (
                <div className="absolute right-0 mt-2 w-48 rounded-lg border border-[#2C2731] bg-[#FFFCEF] shadow-lg z-10">
                  <button
                    onClick={handleExportTable}
                    className="w-full px-4 py-2 text-left text-sm text-[#2C2731] hover:bg-[#F2EAC3] first:rounded-t-lg"
                  >
                    Export Table
                  </button>
                  <button
                    onClick={handleExportAllData}
                    className="w-full px-4 py-2 text-left text-sm text-[#2C2731] hover:bg-[#F2EAC3] last:rounded-b-lg"
                  >
                    Export All Data
                  </button>
                </div>
              )}
            </div>
          )}
          <input
            placeholder="Search guides…"
            value={q}
            onChange={(e) => {
              setPage(1);
              setQ(e.target.value);
            }}
            className="w-full rounded-lg border border-[#FEF6D6] bg-[#FFFCEF] px-3 py-2.5 text-sm text-[#2C2731] placeholder-[#2C2731]/50 focus:border-[#F2EAC3] focus:outline-none focus:ring-2 focus:ring-[#F2EAC3] sm:w-64"
          />
          <button
            aria-label="Open filters"
            onClick={() => {
              setDraftStatus(status);
              setDraftIncomeMin(incomeMin);
              setDraftIncomeMax(incomeMax);
              setShowFilters(true);
            }}
            className="inline-flex items-center justify-center gap-2 rounded-lg border border-[#FEF6D6] bg-[#2C2731] px-3 py-2 text-sm font-medium text-[#FEF6D6] transition-colors hover:bg-[#3C3450]"
          >
            <svg
              width="18"
              height="18"
              viewBox="0 0 24 24"
              fill="none"
              stroke="currentColor"
              strokeWidth="2"
              strokeLinecap="round"
              strokeLinejoin="round"
              className="text-[#FEF6D6]"
            >
              <path d="M3 4h18l-7 8v6l-4 2v-8L3 4z" />
            </svg>
            Filters
          </button>
        </div>
      </div>

      {/* Filter modal */}
      {showFilters && (
        <div className="fixed inset-0 z-50 flex items-start justify-center bg-[#2C2731]/70 px-4 py-16">
          <div className="w-full max-w-xl rounded-2xl border border-[#2C2731] bg-[#FFFCEF] text-[#2C2731] shadow-xl">
            <div className="flex items-center justify-between border-b border-[#2C2731]/20 px-6 py-4">
              <h2 className="text-lg font-medium font-recoleta">Filter Options</h2>
              <button
                onClick={() => setShowFilters(false)}
                className="rounded-md border border-[#2C2731] bg-[#FFFCEF] px-2 py-1 text-sm font-medium text-[#2C2731] transition-colors hover:bg-[#F2EAC3]"
                aria-label="Close filters"
              >
                ✕
              </button>
            </div>
            <div className="space-y-5 px-6 py-5">
              <div className="space-y-2">
                <label className="text-sm font-medium text-[#2C2731]/80">
                  Status
                </label>
                <select
                  value={draftStatus}
                  onChange={(e) => setDraftStatus(e.target.value as any)}
                  className="w-full rounded-lg border border-[#2C2731] bg-[#FFFCEF] px-3 py-2 text-sm text-[#2C2731] focus:border-[#2C2731] focus:outline-none focus:ring-2 focus:ring-[#2C2731]/30"
                >
                  {STATUS_OPTIONS.map((opt) => (
                    <option key={opt} value={opt}>
                      {opt === "all" ? "All statuses" : opt.replace("_", " ")}
                    </option>
                  ))}
                </select>
              </div>

              <div className="space-y-2">
                <label className="text-sm font-medium text-[#2C2731]/80">
                  Seeker Revenue Amount
                </label>
                <div className="flex items-center gap-3">
                  <input
                    type="number"
                    placeholder="Min"
                    value={draftIncomeMin}
                    onChange={(e) => setDraftIncomeMin(e.target.value)}
                    className="w-full rounded-lg border border-[#2C2731] bg-[#FFFCEF] px-3 py-2 text-sm focus:border-[#2C2731] focus:outline-none focus:ring-2 focus:ring-[#2C2731]/30"
                  />
                  <span className="text-[#2C2731]/50">-</span>
                  <input
                    type="number"
                    placeholder="Max"
                    value={draftIncomeMax}
                    onChange={(e) => setDraftIncomeMax(e.target.value)}
                    className="w-full rounded-lg border border-[#2C2731] bg-[#FFFCEF] px-3 py-2 text-sm focus:border-[#2C2731] focus:outline-none focus:ring-2 focus:ring-[#2C2731]/30"
                  />
                </div>
              </div>

              <div className="flex flex-col-reverse gap-3 sm:flex-row sm:items-center sm:justify-between">
                <button
                  onClick={() => {
                    setDraftStatus("all");
                    setDraftIncomeMin("");
                    setDraftIncomeMax("");
                  }}
                  className="rounded-lg border border-[#2C2731] px-4 py-2 text-sm font-medium text-[#2C2731] transition-colors hover:bg-[#F2EAC3]"
                >
                  Clear All
                </button>
                <button
                  onClick={() => {
                    setPage(1);
                    setStatus(draftStatus);
                    setIncomeMin(draftIncomeMin);
                    setIncomeMax(draftIncomeMax);
                    setShowFilters(false);
                  }}
                  className="rounded-lg border border-[#2C2731] bg-[#2C2731] px-4 py-2 text-sm font-medium text-[#FEF6D6] transition-colors hover:bg-[#3C3450]"
                >
                  Apply Filters
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

      <GuideProvisionModal
        isOpen={showCreateGuide}
        onClose={() => setShowCreateGuide(false)}
        onSuccess={(details) => {
          setProvisionAlert({
            type: "success",
            text: details?.tempPassword
              ? `Guide created. Temporary password for ${details.email ?? "new guide"}: ${details.tempPassword}`
              : "Guide created successfully.",
          });
          refreshSamePage();
          setShowCreateGuide(false);
        }}
      />

      {/* Table */}
      <div className="overflow-x-auto rounded-2xl border border-[#2C2731] bg-[#FFFCEF] shadow">
        <table className="min-w-full text-left text-sm">
          <thead className="bg-[#2C2731] text-[#FEF6D6]">
            <tr>
              <th className="px-4 py-3 w-12">
                <input
                  type="checkbox"
                  checked={allSelected}
                  ref={(el) => {
                    if (el) el.indeterminate = someSelected;
                  }}
                  onChange={toggleSelectAll}
                  className="w-4 h-4 rounded border-[#FEF6D6] text-[#2C2731] focus:ring-2 focus:ring-[#FEF6D6]"
                />
              </th>
              <th
                className="px-4 py-3 font-medium uppercase tracking-wide cursor-pointer hover:bg-[#3C3450] transition-colors"
                onClick={() => handleSort("name")}
              >
                <div className="flex items-center gap-2">
                  Guide
                  <SortIcon field="name" />
                </div>
              </th>
              <th
                className="px-4 py-3 font-medium uppercase tracking-wide cursor-pointer hover:bg-[#3C3450] transition-colors"
                onClick={() => handleSort("status")}
              >
                <div className="flex items-center gap-2">
                  Status
                  <SortIcon field="status" />
                </div>
              </th>
              <th className="px-4 py-3 font-medium uppercase tracking-wide">
                Location
              </th>
              <th
                className="px-4 py-3 font-medium uppercase tracking-wide cursor-pointer hover:bg-[#3C3450] transition-colors"
                onClick={() => handleSort("connectedSeekers")}
              >
                <div className="flex items-center gap-2">
                  Connected Seekers
                  <SortIcon field="connectedSeekers" />
                </div>
              </th>
              <th
                className="px-4 py-3 font-medium uppercase tracking-wide cursor-pointer hover:bg-[#3C3450] transition-colors"
                onClick={() => handleSort("income")}
              >
                <div className="flex items-center gap-2">
                  Income
                  <SortIcon field="income" />
                </div>
              </th>
              <th
                className="px-4 py-3 font-medium uppercase tracking-wide cursor-pointer hover:bg-[#3C3450] transition-colors"
                onClick={() => handleSort("joinedAt")}
              >
                <div className="flex items-center gap-2">
                  Joined
                  <SortIcon field="joinedAt" />
                </div>
              </th>
              <th className="px-4 py-3 text-right font-medium uppercase tracking-wide">
                Actions
              </th>
            </tr>
          </thead>
          <tbody className="divide-y divide-[#2C2731]/20">
            {loading && (
              <tr>
                <td
                  colSpan={8}
                  className="px-4 py-6 text-center text-sm text-[#2C2731]/70"
                >
                  Loading…
                </td>
              </tr>
            )}

            {!loading && sortedItems.length === 0 && (
              <tr>
                <td
                  colSpan={8}
                  className="px-4 py-6 text-center text-sm text-[#2C2731]/70"
                >
                  No guides found
                </td>
              </tr>
            )}

            {!loading &&
              sortedItems.map((g) => (
                <tr key={g.id} className="bg-[#FFFCEF] text-[#2C2731]">
                  <td className="px-4 py-4 align-top">
                    <input
                      type="checkbox"
                      checked={selectedIds.has(g.id)}
                      onChange={() => toggleSelect(g.id)}
                      className="w-4 h-4 rounded border-[#2C2731] text-[#2C2731] focus:ring-2 focus:ring-[#2C2731]"
                    />
                  </td>
                  <td className="px-4 py-4 align-top">
                    <div className="font-medium text-[#2C2731] ph-mask">{g.name}</div>
                    <div className="text-xs text-[#2C2731]/70 ph-mask">{g.email}</div>
                  </td>
                  <td className="px-4 py-4 align-top">
                    <GuideStatusBadge status={g.status} />
                  </td>
                  <td className="px-4 py-4 align-top text-[#2C2731]/70">
                    {g.region || "—"}
                  </td>
                  <td className="px-4 py-4 align-top">{g.connectedSeekers}</td>
                  <td className="px-4 py-4 align-top">
                    ${formatIncome(g.income)}
                  </td>
                  <td className="px-4 py-4 align-top">
                    {new Date(g.joinedAt).toLocaleDateString()}
                  </td>
                  <td className="px-4 py-4 text-right align-top">
                    <div className="flex items-center justify-end gap-2">
                      <button
                        onClick={() => setOpenId(g.id)}
                        className="rounded-lg border border-[#2C2731] bg-[#2C2731] px-3 py-2 text-xs font-semibold text-[#FEF6D6] transition-colors hover:bg-[#3C3450]"
                      >
                        Manage
                      </button>
                      <button
                        onClick={() => setDeleteTarget(g)}
                        className="rounded-lg border border-[#DC2626] bg-[#DC2626] px-3 py-2 text-xs font-semibold text-white transition-colors hover:bg-[#B91C1C]"
                      >
                        Delete
                      </button>
                    </div>
                  </td>
                </tr>
              ))}
          </tbody>
        </table>
      </div>

      {/* Pagination */}
      <div className="flex flex-col gap-3 text-sm text-[#2C2731]/80 md:flex-row md:items-center md:justify-between">
        <div>Total: {res?.total ?? 0}</div>
        <div className="flex items-center gap-3">
          <button
            disabled={page <= 1}
            onClick={() => setPage((p) => Math.max(1, p - 1))}
            className={`rounded-lg border border-[#2C2731] px-3 py-2 font-medium transition-colors ${
              page <= 1
                ? "cursor-not-allowed bg-[#F2EAC3] text-[#2C2731]/40"
                : "bg-[#2C2731] text-[#FEF6D6] hover:bg-[#3C3450]"
            }`}
          >
            Previous
          </button>
          <div className="px-2 text-sm text-[#2C2731]">
            Page {page} / {totalPages}
          </div>
          <button
            disabled={page >= totalPages}
            onClick={() => setPage((p) => Math.min(totalPages, p + 1))}
            className={`rounded-lg border border-[#2C2731] px-3 py-2 font-medium transition-colors ${
              page >= totalPages
                ? "cursor-not-allowed bg-[#F2EAC3] text-[#2C2731]/40"
                : "bg-[#2C2731] text-[#FEF6D6] hover:bg-[#3C3450]"
            }`}
          >
            Next
          </button>
        </div>
      </div>

      {/* Detail modal */}
      {openId && (
        <GuideDetailModal
          id={openId}
          onClose={() => setOpenId(null)}
          onUpdated={() => {
            setOpenId(null);
            refreshSamePage();
          }}
        />
      )}

      {/* Delete modal */}
      {deleteTarget && (
        <DeleteUserModal
          userId={deleteTarget.id}
          userName={deleteTarget.name || deleteTarget.email}
          role="guide"
          isActive={deleteTarget.status === "active"}
          hasActiveMatch={(deleteTarget.connectedSeekers ?? 0) > 0}
          onClose={() => setDeleteTarget(null)}
          onDeleted={() => {
            setDeleteTarget(null);
            refreshSamePage();
          }}
        />
      )}
    </div>
  );
}
